import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AnnouncementIcon from '@mui/icons-material/Announcement'
import BusinessIcon from '@mui/icons-material/Business'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import DateRangeIcon from '@mui/icons-material/DateRange'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HomeIcon from '@mui/icons-material/Home'
import LogoutIcon from '@mui/icons-material/Logout'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import PriceCheckIcon from '@mui/icons-material/PriceCheck'
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
  useTheme
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router'
import { useRecoilValue } from 'recoil'

import { isPricingEnabled } from '../../config/environment'
import routes from '../../pages/routes'
import { sessionState } from '../../state'
import Database from '../../types/Database'

const packageJson = require('../../../package.json')

const drawerWidth = 240

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start'
}))

interface NavigationDrawerProps {
  open: boolean
  user: Database.User | null
  onClose: () => void
  onSignOut: () => void
  onToggleDarkMode: () => void
  onOpenPricingModal: () => void
}

/**
 * NavigationDrawer - Side navigation drawer component
 *
 * Contains all navigation menu items and user actions.
 * Handles navigation and user preferences.
 */
export default function NavigationDrawer({
  open,
  user,
  onClose,
  onSignOut,
  onToggleDarkMode,
  onOpenPricingModal
}: NavigationDrawerProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const session = useRecoilValue(sessionState)

  // Hide pricing when viewing an office the user doesn't own
  const isViewingNonOwnedOffice = (() => {
    const match = location.pathname.match(/^\/office\/([^/]+)/)
    if (!match) return false
    const officeId = match[1]
    const office = session.offices?.find(o => o.oid === officeId)
    return !office || office.uid !== user?.uid
  })()

  const showPricing = isPricingEnabled() && !isViewingNonOwnedOffice

  const handleNavigation = (path: string) => {
    navigate(path, { replace: true })
  }

  const handleSignOut = async () => {
    await onSignOut()
    navigate(routes.signin, { replace: true })
  }

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      variant='persistent'
      anchor='right'
      open={open}
    >
      <DrawerHeader>
        <IconButton onClick={onClose}>
          {theme.direction === 'rtl' ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </DrawerHeader>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {user && (
          <List>
            <ListItem
              key='home'
              disablePadding
              onClick={() => handleNavigation(routes.home)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary='Home' />
              </ListItemButton>
            </ListItem>

            <ListItem
              key='book'
              disablePadding
              onClick={() => handleNavigation(routes.book)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <DateRangeIcon />
                </ListItemIcon>
                <ListItemText primary='Book' />
              </ListItemButton>
            </ListItem>

            <ListItem
              key='offices'
              disablePadding
              onClick={() => handleNavigation(routes.offices)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary='Offices' />
              </ListItemButton>
            </ListItem>

            {/*
              People nav item is intentionally visible to all authenticated users
              (same as Offices/Book/Profile). The People page handles the empty state
              gracefully by showing a "You are not a manager" message, and any user
              could become a manager at any time. This avoids the cost of querying
              managed offices in the navigation drawer on every render.
            */}
            <ListItem
              key='people'
              disablePadding
              onClick={() => handleNavigation(routes.people)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary='People' />
              </ListItemButton>
            </ListItem>

            <ListItem
              key='notifications'
              disablePadding
              onClick={() => handleNavigation(routes.notifications)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <AnnouncementIcon />
                </ListItemIcon>
                <ListItemText primary='Notifications' />
              </ListItemButton>
            </ListItem>

            <ListItem
              key='profile'
              disablePadding
              onClick={() => handleNavigation(routes.profile)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary='Profile' />
              </ListItemButton>
            </ListItem>

            {user.isAdmin && (
              <ListItem
                key='admin'
                disablePadding
                onClick={() => handleNavigation(routes.admin)}
              >
                <ListItemButton>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary='Admin' />
                </ListItemButton>
              </ListItem>
            )}

            {showPricing && (
              <ListItem
                key='pricing'
                disablePadding
                onClick={onOpenPricingModal}
              >
                <ListItemButton>
                  <ListItemIcon>
                    <PriceCheckIcon />
                  </ListItemIcon>
                  <ListItemText primary='Pricing' />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem key='darkmode' disablePadding onClick={onToggleDarkMode}>
              <ListItemButton>
                <ListItemIcon>
                  <DarkModeIcon />
                </ListItemIcon>
                <ListItemText primary='Toggle dark mode' />
              </ListItemButton>
            </ListItem>
          </List>
        )}

        <Divider />

        {user && (
          <List>
            <ListItem
              key='help'
              disablePadding
              onClick={() => handleNavigation(routes.help)}
            >
              <ListItemButton>
                <ListItemIcon>
                  <HelpOutlineIcon />
                </ListItemIcon>
                <ListItemText primary='Help' />
              </ListItemButton>
            </ListItem>

            <ListItem key='signout' disablePadding onClick={handleSignOut}>
              <ListItemButton>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary='Sign out' />
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          textAlign: 'center',
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant='caption' color='text.secondary'>
          v{packageJson.version}
        </Typography>
      </Box>
    </Drawer>
  )
}
